import {
  Divider,
  Icon,
  LinkifiedText,
  SuccessBadge,
  Text,
} from "@certn/cascadia-react";
import { css, cx } from "@emotion/css";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DocumentResponseField } from "./DocumentResponseField";
import { DocumentRow } from "./DocumentRow";
import { SubmitItemButton } from "./SubmitItemButton";
import { InformationRequestItem, InformationRequestItemAnswer } from "./types";
import { isItemSubmitted } from "./utils";
import { WrittenResponseField } from "./WrittenResponseField";

type Props = {
  caseId: string;
  item: InformationRequestItem;
  itemNumber: number;
  isMarkedUnanswered: boolean;
  onAnswered: (answer: InformationRequestItemAnswer) => void;
  onDocumentsChanged: () => Promise<unknown>;
  onItemUnanswered: () => void;
  onRequestUpdated: () => Promise<unknown>;
  onListRefresh: () => Promise<unknown>;
};

export function InformationRequestItemView({
  caseId,
  item,
  itemNumber,
  isMarkedUnanswered,
  onAnswered,
  onDocumentsChanged,
  onItemUnanswered,
  onRequestUpdated,
  onListRefresh,
}: Props) {
  const { t } = useTranslation();
  const isDocument = item.response_type === "DOCUMENT";
  const isSubmitted = isItemSubmitted(item);
  const [hasDraft, setHasDraft] = useState(false);
  const saveDraftRef = useRef<(() => Promise<boolean>) | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasSubmitted = useRef(isSubmitted);
  const panelId = useId();

  useEffect(() => {
    if (isSubmitted && !wasSubmitted.current) {
      setIsOpen(false);
      toggleRef.current?.focus();
    }
    wasSubmitted.current = isSubmitted;
  }, [isSubmitted]);

  const instructions = (
    <LinkifiedText
      text={item.instructions}
      testID={`information-request-item-${item.id}-instructions`}
    />
  );

  return (
    <section
      className={styles.item}
      aria-label={item.instructions}
      data-testid={`information-request-item-${item.id}`}
      data-item-id={item.id}
    >
      <Divider />
      {isSubmitted ? (
        <h3 className={styles.toggleHeading}>
          <div className={styles.toggle}>
            <span className={styles.header}>
              <span className={styles.label}>
                <Text
                  style="input/label"
                  color="text-weak"
                  testID={`information-request-item-${item.id}-number`}
                >
                  {t("Request {{number}}", { number: itemNumber })}
                </Text>
                <SuccessBadge
                  type="subtle"
                  testID={`information-request-item-${item.id}-submitted`}
                >
                  {t("Submitted")}
                </SuccessBadge>
              </span>
              <Text
                as="span"
                style="global/heading-medium"
                className={styles.heading}
              >
                {instructions}
              </Text>
            </span>
            <Icon
              name="expand_more"
              size="space-6"
              className={cx(styles.chevron, isOpen && styles.chevronOpen)}
            />
            <button
              ref={toggleRef}
              type="button"
              className={styles.toggleControl}
              aria-expanded={isOpen}
              aria-controls={panelId}
              aria-label={t("Request {{number}}", { number: itemNumber })}
              data-testid={`information-request-item-${item.id}-toggle`}
              onClick={() => setIsOpen((open) => !open)}
            />
          </div>
        </h3>
      ) : (
        <div className={styles.header}>
          <div className={styles.label}>
            <Text
              style="input/label"
              color="text-weak"
              testID={`information-request-item-${item.id}-number`}
            >
              {t("Request {{number}}", { number: itemNumber })}
            </Text>
            {isSubmitted && (
              <SuccessBadge
                type="subtle"
                testID={`information-request-item-${item.id}-submitted`}
              >
                {t("Submitted")}
              </SuccessBadge>
            )}
          </div>
          <Text
            as="h3"
            style="global/heading-medium"
            className={styles.heading}
          >
            {instructions}
          </Text>
        </div>
      )}
      {(!isSubmitted || isOpen) && (
        <div id={panelId} className={styles.item}>
          {item.attachments.length > 0 && (
            <div
              className={styles.attachments}
              data-testid={`information-request-item-${item.id}-attachments`}
            >
              <Text style="input/label">{t("Attachments")}</Text>
              {item.attachments.map((attachment) => (
                <DocumentRow
                  key={attachment.id}
                  caseId={caseId}
                  document={attachment}
                />
              ))}
            </div>
          )}
          {isDocument ? (
            <DocumentResponseField
              caseId={caseId}
              item={item}
              isReadOnly={isSubmitted}
              onAnswered={onAnswered}
              onDocumentsChanged={onDocumentsChanged}
              onDraftChange={setHasDraft}
              saveDraftRef={saveDraftRef}
            />
          ) : (
            <WrittenResponseField
              caseId={caseId}
              item={item}
              isReadOnly={isSubmitted}
              onAnswered={onAnswered}
              onDraftChange={setHasDraft}
              saveDraftRef={saveDraftRef}
            />
          )}
          {!isSubmitted && (
            <SubmitItemButton
              caseId={caseId}
              itemId={item.id}
              isAnswered={item.is_answered || hasDraft}
              hasSavedAnswer={item.is_answered}
              saveDraft={() =>
                saveDraftRef.current?.() ?? Promise.resolve(true)
              }
              isMarkedUnanswered={isMarkedUnanswered}
              onRequestUpdated={onRequestUpdated}
              onUnanswered={onItemUnanswered}
              onListRefresh={onListRefresh}
            />
          )}
        </div>
      )}
    </section>
  );
}

const styles = {
  item: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  label: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  header: css({
    display: "flex",
    minWidth: 0,
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  toggleHeading: css({ margin: 0 }),
  toggle: css({
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "var(--space-4)",
  }),
  toggleControl: css({
    position: "absolute",
    inset: 0,
    margin: 0,
    padding: 0,
    border: 0,
    background: "transparent",
    cursor: "pointer",
    borderRadius: "var(--space-1)",
    "&:focus-visible": {
      outline: "2px solid var(--border-focus, currentColor)",
      outlineOffset: "2px",
    },
  }),
  chevron: css({
    flexShrink: 0,
    transition: "transform 0.2s ease-out",
  }),
  chevronOpen: css({ transform: "rotate(180deg)" }),
  heading: css({
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    "& a": {
      position: "relative",
      zIndex: 1,
    },
  }),
  attachments: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
};
